import redirect from 'js/lib/coursera.redirect';
import type UserAgentInfo from 'js/lib/useragent';

import { track } from '@coursera/event-pulse/sdk';

import {
  ACCOUNT_PAGE_IDENTIFIER,
  CERTIFICATE_PAGE_IDENTIFIER,
  CONSUMER_EXPLORE_PAGE_IDENTIFIER,
  DEGREES_PAGE_IDENTIFIER,
  DOMAIN_PAGE_IDENTIFIER,
  EARN_ONLINE_DEGREEE_PAGE_IDENTIFIER,
  EARN_PROFESSIONAL_CERTIFICATE_PAGE_IDENTIFIER,
  ENTERPRISE_EXPLORE_PAGE_IDENTIFIER,
  EXPLORE_CATEGORIES_PAGE_IDENTIFIER,
  EXPLORE_PAGE_IDENTIFIER,
  EXPLORE_ROLES_PAGE_IDENTIFIER,
  EXPLORE_SKILLS_PAGE_IDENTIFIER,
  EXPLORE_SUBMENU_PAGE_IDENTIFIER,
  LANGUAGE_TRANSLATION_FEEDBACK_PAGE_IDENTIFIER,
  LANGUAGE_TRANSLATION_SUBMENU_PAGE_IDENTIFIER,
  PREMIUM_PROGRAMS_PAGE_IDENTIFIER,
  TRANSITION_DELAY,
} from 'bundles/page-header/components/mobile/constants';
import type { NavButtonType } from 'bundles/page-header/components/mobile/constants';
import { selectProgramHomepage } from 'bundles/page/api/selectProgramHomePage';
import { getAppUrl } from 'bundles/page/lib/mobileAppLink';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const refIdentifierList = [
  ACCOUNT_PAGE_IDENTIFIER,
  LANGUAGE_TRANSLATION_SUBMENU_PAGE_IDENTIFIER,
  LANGUAGE_TRANSLATION_FEEDBACK_PAGE_IDENTIFIER,
  CERTIFICATE_PAGE_IDENTIFIER,
  DEGREES_PAGE_IDENTIFIER,
  DOMAIN_PAGE_IDENTIFIER,
  EXPLORE_PAGE_IDENTIFIER,
  EXPLORE_SUBMENU_PAGE_IDENTIFIER,
  PREMIUM_PROGRAMS_PAGE_IDENTIFIER,
  ENTERPRISE_EXPLORE_PAGE_IDENTIFIER,
  CONSUMER_EXPLORE_PAGE_IDENTIFIER,
  EXPLORE_ROLES_PAGE_IDENTIFIER,
  EXPLORE_CATEGORIES_PAGE_IDENTIFIER,
  EXPLORE_SKILLS_PAGE_IDENTIFIER,
  EARN_PROFESSIONAL_CERTIFICATE_PAGE_IDENTIFIER,
  EARN_ONLINE_DEGREEE_PAGE_IDENTIFIER,
] as const;

export type refIdentifiers = (typeof refIdentifierList)[number];

export function filterUndefined<T>(item: T | undefined | null | false): item is T {
  return !!(item as T);
}

export const isAndroidOrIOSFromUserAgent = (userAgent: UserAgentInfo) => {
  return userAgent && (userAgent.isIOS || userAgent.isAndroid);
};

export const returnIOSButtonIfApplicableFromUserAgent = (
  userAgent: UserAgentInfo,
  pathname: string,
  requestCountryCode: string
): NavButtonType | null => {
  if (!isAndroidOrIOSFromUserAgent(userAgent)) return null;
  const appLink = getAppUrl(userAgent, pathname, requestCountryCode);
  return { href: appLink, label: 'Get our app', name: 'get-app' };
};

export const getTabIndex = (showNav: boolean) => (showNav ? 0 : -1);

export const redirectToMyCourseraAndUpdateUserProgram = ({ userId }: { userId: number | string }) => {
  selectProgramHomepage(userId.toString(), 'COURSERA')
    .then(() => redirect.setLocation('/'))
    .done();
};

export const mapDegreesAndProgramsToNavButtonObjects = ({
  degrees,
  programs,
  previousHomepage,
  previousHomepageType,
}: $TSFixMe) => {
  let degreeButtonObjs = [];
  let programButtonObjs = [];

  // .reverse() is currently being used to conform this util function to the previous
  // unshift-based accumulation implementation in it's previous location. This is used
  // to preserve the previous ordering.
  if (degrees.length) {
    degreeButtonObjs = degrees.reverse().map(({ name, slug, id }: $TSFixMe) => ({
      href: `/degrees/${slug}/home`,
      label: name,
      name,
      onButtonClick: () => {
        track('switch_homepage', {
          previousHomepage,
          currentHomepage: id,
          currentHomepageType: 'degree',
          previousHomepageType,
        });
      },
    }));
  }
  if (programs.length) {
    programButtonObjs = programs
      .reverse()
      .map(({ metadata: { name, slug, squareLogo, rectangularLogo }, thirdPartyOrganization, id }: $TSFixMe) => ({
        href: `/programs/${slug}`,
        label: name,
        name,
        squareLogo,
        rectangularLogo,
        thirdPartyOrganization,
        onButtonClick: () => {
          track('switch_homepage', {
            previousHomepage,
            currentHomepage: id,
            currentHomepageType: 'enterprise_program',
            previousHomepageType,
          });
        },
      }));
  }

  return [...programButtonObjs, ...degreeButtonObjs];
};

export function subPageFocusAndOffsetFix() {
  setTimeout(() => {
    // move keyboard focus to the submenu
    const headerElement = document.getElementsByClassName('mobile-submenu-header')[0] as HTMLElement;
    headerElement?.focus();

    // fix for mobile megamenu offset issue upon opening submenu
    const domainsMenu = document.getElementsByClassName('rc-domainMenu')[0];
    if (domainsMenu) domainsMenu.scrollTop = 1;
    const degreeMenu = document.getElementsByClassName('rc-DegreeMenu')[0];
    if (degreeMenu) degreeMenu.scrollTop = 1;
    const certificateMenu = document.getElementsByClassName('rc-CertificateMenu')[0];
    if (certificateMenu) certificateMenu.scrollTop = 1;
    const mainMenu = document.querySelectorAll('.mobile_logged_out_nav div')[0];
    if (mainMenu) mainMenu.scrollTop = 1;
    const navWrapper = document.getElementsByClassName('nav-transition-wrapper')[0];
    if (navWrapper) navWrapper.scrollTop = 1;
  }, TRANSITION_DELAY);
}
